/* eslint-disable no-useless-escape */
import { Avatar, Button, Image, Input } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../../app/store";
import AttachmentIcon from "../../../../../../assets/icons/AttachmentIcon";
import CloseIcon from "../../../../../../assets/icons/CloseIcon";
import SendIcon from "../../../../../../assets/icons/SendIcon";
import AvatarImage from "../../../../../../assets/images/avatar.svg";
import { useFileUploadMutation } from "../../../../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useCreateCommentMutation,
  useUpdateCommentMutation,
} from "../../../../../../features/superAdmin/ticketService/ticket/ticket.api";
import useError from "../../../../../../hooks/useError";
import { toast } from "../../../../../../lib/toast";
import { TabProps } from "./TicketDetailsTabs";

const TicketCommentsTab = ({ ticketDetails }: TabProps) => {
  const [selectedComment, setSelectedComment] = useState<string | undefined>(
    undefined,
  );
  const [comment, setComment] = useState<string | undefined>("");

  const [attachment, setAttachment] = useState("");
  const [updateId, setUpdateId] = useState("");
  const { user } = useAppSelector((state) => state.auth);
  const [addComment, { isLoading, isError, error, isSuccess }] =
    useCreateCommentMutation();
  useError(isError, error, "Send comment fail");

  const [
    updateComment,
    { isError: isUpdateError, error: updateError, isSuccess: isUpdateSuccess },
  ] = useUpdateCommentMutation();
  const [
    fileUpload,
    {
      error: fileUploadError,
      isError: fileUploadIsError,
      isLoading: isFileUploading,
    },
  ] = useFileUploadMutation();
  useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  useError(isError, error, "Send comment fail");
  useError(isUpdateError, updateError, "Comment update fail");

  const comments = ticketDetails?.comments?.map((item: any) => ({
    id: item?.id,
    name: item?.user?.officialInfo?.name,
    text: item?.comment,
    daysAgo: moment(item?.created_at).fromNow(),
    avatar: item?.user?.personalInfo?.pro_pic || AvatarImage,
    image: item?.attachment || null,
    userId: item?.user?.id,
    old_comment: item?.old_comment,
    old_attachment: item?.old_attachment,
  }));

  const handleComment = () => {
    if (updateId) {
      updateComment({
        id: updateId,
        data: {
          ticketLogId: Number(ticketDetails?.id),
          userId: user?.id,
          comment: selectedComment || "",
          attachment: attachment || null,
        },
      });
    } else {
      addComment({
        ticketLogId: Number(ticketDetails?.id),
        userId: user?.id,
        comment: comment || "",
        attachment: attachment || null,
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const maxFileSize = 32 * 1024 * 1024;

    const file = files[0];

    if (file.size > maxFileSize) {
      toast("error", `\"${file.name}\" exceeds max file size of 32MB.`);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    fileUpload(formData)
      .unwrap()
      .then((res: any) => {
        if (res?.Location) {
          setAttachment(res.Location);
        }
      })
      .catch((err) => {
        console.error("Image upload failed", err);
      });

    e.target.value = "";
  };

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      setSelectedComment("");
      setAttachment("");
      setUpdateId("");
      setComment("");
    }
  }, [isSuccess, isUpdateSuccess]);

  useEffect(() => {
    if (updateId) {
      const findComment = comments?.find((item: any) => item?.id === updateId);
      setSelectedComment(findComment?.text);
      setAttachment(findComment?.image);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateId]);

  return (
    <>
      <div>
        <div
          className={`${
            attachment ? "h-[calc(100vh_-_570px)]" : "h-[calc(100vh_-_460px)]"
          } w-full overflow-auto px-1`}
        >
          {comments?.map((comment: any) => {
            return (
              <div
                key={comment.id}
                className="flex items-start space-x-3 mb-4 bg-white shadow p-4 rounded-md"
              >
                <Avatar
                  className="object-cover"
                  src={comment.avatar}
                  size={40}
                />
                <div className="w-full flex-1">
                  <div className="flex gap-1 text-base items-center">
                    <span className="font-semibold">{comment.name}</span>
                    <span className="text-[#5E718D] text-xs">
                      {comment.daysAgo}
                    </span>
                    {(comment?.old_comment || comment?.old_attachment) && (
                      <span className="text-[#5E718D] text-xs">| Edited</span>
                    )}
                  </div>
                  {updateId && comment.id === updateId ? (
                    <>
                      <div>
                        <div className="mb-1 mt-5 rounded-md bg-[#F9FAFB] border border-[#D7DFE9]">
                          <div className="p-3">
                            <Input
                              placeholder="Your Message"
                              className="border-transparent focus:ring-0 focus:outline-none flex-1"
                              value={selectedComment}
                              onChange={(e) =>
                                setSelectedComment(e.target.value)
                              }
                              onPressEnter={(e) => {
                                e.preventDefault();
                                if (selectedComment && !isFileUploading) {
                                  handleComment();
                                }
                              }}
                            />
                            {attachment && (
                              <div className="w-[150px] relative">
                                <div
                                  className="absolute top-5 right-3 z-10 cursor-pointer"
                                  onClick={() => setAttachment("")}
                                >
                                  <CloseIcon />
                                </div>
                                <Image
                                  width={150}
                                  height={100}
                                  src={attachment}
                                  style={{ objectFit: "cover" }}
                                  rootClassName="rounded-md "
                                  wrapperClassName="mt-2 rounded-md "
                                  className="rounded-md object-cover border border-light border-solid"
                                />
                              </div>
                            )}
                          </div>

                          <label
                            htmlFor="attachment"
                            className=" bg-[#D7DFE9] block w-full px-3 py-2.5"
                          >
                            <input
                              type="file"
                              id="attachment"
                              className="hidden"
                              onChange={handleImageChange}
                              accept="image/*"
                            />
                            <AttachmentIcon />
                          </label>
                        </div>
                        <div className="mt-1">
                          <button
                            onClick={() => {
                              setSelectedComment("");
                              setComment("");
                              setAttachment("");
                              setUpdateId(undefined as unknown as string);
                            }}
                            className="text-[#455468] text-sm inline-block mr-5"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleComment}
                            className="text-[#455468] text-sm inline-block"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {comment.text && (
                        <p className="text-[#3D4A5C] text-base">
                          {comment.text}
                        </p>
                      )}
                      {comment.image && (
                        <Image
                          width={200}
                          height={100}
                          src={comment.image}
                          style={{ objectFit: "cover" }}
                          rootClassName="rounded-md "
                          wrapperClassName="mt-2 rounded-md "
                          className="rounded-md object-cover border border-light border-solid"
                        />
                      )}
                      {comment?.userId === user?.id ? (
                        <div className="flex space-x-4 text-[#455468] text-sm mt-1 cursor-pointer">
                          <span onClick={() => setUpdateId(comment?.id)}>
                            Edit
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          {attachment && !updateId && (
            <div className="w-[150px] relative ml-3">
              <div
                className="absolute top-5 right-3 z-10 cursor-pointer"
                onClick={() => setAttachment("")}
              >
                <CloseIcon />
              </div>
              <Image
                width={150}
                height={100}
                src={attachment}
                style={{ objectFit: "cover" }}
                rootClassName="rounded-md "
                wrapperClassName="mt-2 rounded-md "
                className="rounded-md object-cover border border-light border-solid"
              />
            </div>
          )}
          <div className="flex items-center gap-2 rounded-md px-3 py-2">
            <div className="flex items-center border border-[#D7DFE9] rounded-lg w-full">
              {/* Prefix (Attachment Icon) */}
              <label htmlFor="attachment" className="cursor-pointer p-2.5 pr-0">
                <input
                  type="file"
                  id="attachment"
                  className="hidden"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                <AttachmentIcon />
              </label>

              {/* AntD TextArea */}
              <Input.TextArea
                placeholder="Your Message"
                autoSize={{ minRows: 1, maxRows: 5 }}
                className="border-none focus:ring-0 focus:outline-none resize-none !outline-none !right-0"
                classNames={{ textarea: "!h-9" }}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if ((comment || attachment) && !isFileUploading) {
                      handleComment();
                    }
                  }
                }}
              />
            </div>
            <Button
              className="!size-10 flex items-center justify-center disabled:!border-primary"
              classNames={{icon:'flex items-center justify-center mt-[1px]'}}
              type="primary"
              icon={<SendIcon />}
              loading={isLoading}
              disabled={!(comment || attachment) || isFileUploading}
              onClick={handleComment}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketCommentsTab;
